import React from 'react';
import type { ElementRef } from 'react';

import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Slot, SlotProps } from '@radix-ui/react-slot';

import { styled } from '@backyard-ui/styles';

import { baseStyles } from './Icon.styles';

export interface IconProps extends SlotProps {
  /**
   * The accessible label for the icon. This label will be visually hidden but announced to screen
   * reader users, similar to `alt` text for `img` tags.
   */
  label?: string;

  /**
   * The Icon color.
   */
  color?:
    | 'critical'
    | 'info'
    | 'primary'
    | 'secondary'
    | 'warning'
    | 'black'
    | 'white';

  /**
   * Define the icon size.
   */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * The html role of the icon.
   */
  role?: 'presentation' | 'img';

  /**
   * The ref to the HTML DOM element
   */
  ref?: any;
}

const IconEl = styled(Slot, baseStyles);

const Icon = React.forwardRef<ElementRef<'svg'>, IconProps>((props, ref) => {
  const { children, label, role = 'presentation' } = props;

  const child = React.isValidElement(children)
    ? React.Children.only(children)
    : null;

  return (
    <>
      <IconEl ref={ref} {...props}>
        {React.cloneElement(child as React.ReactElement, {
          'aria-hidden': 'true',
          focusable: 'false',
          role,
        })}
      </IconEl>
      {label && (
        <VisuallyHidden.Root data-testid="Icon-label">
          {label}
        </VisuallyHidden.Root>
      )}
    </>
  );
});

Icon.displayName = 'Icon';

export default Icon;
